import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import AppShell from "./components/AppShell";
import { hasAdminToken } from "./lib/adminToken";
import { initAnalytics, trackPageview } from "./lib/analytics";

const EmbedStoryPage = lazy(() => import("./pages/EmbedStoryPage"));
const Landing = lazy(() => import("./pages/Landing"));
const Explore = lazy(() => import("./pages/Explore"));
const Reports = lazy(() => import("./pages/Reports"));
const Licensing = lazy(() => import("./pages/Licensing"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Account = lazy(() => import("./pages/Account"));
const Docs = lazy(() => import("./pages/Docs"));
const Contact = lazy(() => import("./pages/Contact"));
const Trends = lazy(() => import("./pages/Trends"));
const DevConsole = lazy(() => import("./pages/DevConsole"));
const About = lazy(() => import("./pages/About"));
const Faq = lazy(() => import("./pages/Faq"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const Aquaculture = lazy(() => import("./pages/Aquaculture"));
const Scorecard = lazy(() => import("./pages/Scorecard"));
const Launch = lazy(() => import("./pages/Launch"));

function AdminRoute({ children }: { children: React.ReactNode }) {
  return hasAdminToken() ? <>{children}</> : <Navigate to="/dev" replace />;
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Deep links like /licensing redirect to /pricing#licensing, but the
  // target page is lazy-loaded, so the element doesn't exist yet on the
  // first render — retry across frames until it mounts (or give up).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let rafId: number;
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < 60) rafId = requestAnimationFrame(tryScroll);
    };
    tryScroll();
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname, location.hash]);

  // Embed pages render without AppShell chrome (no nav/header — safe to iframe)
  if (location.pathname.startsWith("/embed/")) {
    return (
      <Suspense fallback={null}>
        <Routes location={location}>
          <Route path="/embed/story/:id" element={<EmbedStoryPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AppShell>
      <div key={location.pathname} className="page-swap">
        <Suspense
          fallback={
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-6 text-sm text-white/70">
              Loading BluePulse surface...
            </div>
          }
        >
          <Routes location={location}>
            <Route path="/" element={<Landing />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/licensing" element={<Licensing />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/aquaculture" element={<Aquaculture />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/billing/success" element={<BillingSuccess />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/trends" element={<Trends />} />
            <Route path="/dev" element={<DevConsole />} />
            <Route path="/scorecards" element={<AdminRoute><Scorecard /></AdminRoute>} />
            <Route path="/launch" element={<Launch />} />
            <Route path="/scorecard" element={<Navigate to="/scorecards" replace />} />
          </Routes>
        </Suspense>
      </div>
    </AppShell>
  );
}
